import { db } from './index';
import { queueRepo } from './queue';

function nowIso() {
  return new Date().toISOString();
}

function uuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function toClient(row) {
  if (!row) return row;
  const { syncStatus, localUpdatedAt, deletedAt, syncError, ...habit } = row;
  void syncStatus; void localUpdatedAt; void deletedAt; void syncError;
  return habit;
}

export const habitsRepo = {
  async list(userId) {
    const rows = await db.habits
      .where('userId')
      .equals(userId)
      .filter((h) => !h.deletedAt)
      .toArray();
    return rows.map(toClient);
  },

  async get(userId, habitId) {
    const row = await db.habits.get(habitId);
    if (!row || row.userId !== userId || row.deletedAt) return undefined;
    return toClient(row);
  },

  async create(userId, data) {
    const now = nowIso();
    const habit = {
      ...data,
      habitId: `local-${uuid()}`,
      userId,
      createdAt: now,
      lastUpdatedAt: now,
      syncStatus: 'local',
      localUpdatedAt: now,
      deletedAt: null,
    };
    await db.habits.add(habit);
    await queueRepo.enqueue({
      entity: 'habit',
      op: 'create',
      recordId: habit.habitId,
      // clientId lives in the PAYLOAD (not just recordId) so it survives the
      // recordId rewrite that happens after a create lands — guaranteeing the
      // server dedupes a re-delivered/duplicated create to the same habit.
      payload: { ...data, clientId: habit.habitId },
    });
    return toClient(habit);
  },

  async update(userId, habitId, patch) {
    const existing = await db.habits.get(habitId);
    if (!existing || existing.userId !== userId) throw new Error('Habit not found');
    const now = nowIso();
    const next = {
      ...existing,
      ...patch,
      habitId: existing.habitId,
      userId: existing.userId,
      lastUpdatedAt: now,
      syncStatus: 'local',
      localUpdatedAt: now,
    };
    await db.habits.put(next);
    await queueRepo.enqueue({
      entity: 'habit',
      op: 'update',
      recordId: habitId,
      payload: patch,
    });
    return toClient(next);
  },

  async softDelete(userId, habitId) {
    const existing = await db.habits.get(habitId);
    if (!existing || existing.userId !== userId) return;
    const now = nowIso();
    await db.habits.put({
      ...existing,
      deletedAt: now,
      syncStatus: 'local',
      localUpdatedAt: now,
    });
    await queueRepo.enqueue({
      entity: 'habit',
      op: 'delete',
      recordId: habitId,
      payload: null,
    });
  },

  async hardDelete(habitId) {
    await db.habits.delete(habitId);
  },

  async upsertFromServer(record) {
    const existing = await db.habits.get(record.habitId);
    if (existing && existing.syncStatus !== 'synced' && existing.syncStatus !== 'local') {
      return;
    }
    if (existing && existing.syncStatus === 'local') {
      return;
    }
    const localUpdated = existing?.localUpdatedAt ?? '';
    const serverUpdated = record.lastUpdatedAt ?? record.updatedAt ?? nowIso();
    if (localUpdated && localUpdated > serverUpdated) return;
    await db.habits.put({
      ...record,
      syncStatus: 'synced',
      localUpdatedAt: serverUpdated,
      deletedAt: null,
    });
  },

  async rekey(localId, realId) {
    const row = await db.habits.get(localId);
    if (!row) return;
    await db.transaction('rw', db.habits, db.checkins, async () => {
      await db.habits.delete(localId);
      await db.habits.put({ ...row, habitId: realId, syncStatus: 'synced' });
      // Rewrite any local checkins that point at the old habitId.
      const linkedCheckins = await db.checkins.where('habitId').equals(localId).toArray();
      for (const c of linkedCheckins) {
        await db.checkins.delete(c.id);
        const newId = `${realId}#${c.date}`;
        await db.checkins.put({ ...c, id: newId, habitId: realId });
      }
    });
    await queueRepo.rewriteRecordId('habit', localId, realId);
    await queueRepo.rewriteCheckinRecordId(localId, realId);
  },

  async clearForUser(userId) {
    await db.habits.where('userId').equals(userId).delete();
  },
};
